/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { withTranslation } from 'react-i18next';

import {
    InternalGrid,
    Text,
    ThemeDefault,
    CoinsIcon,
    DaButton,
    HeartIcon,
    MarkerIcon,
    SkeletonItem,
    ModalProvider,
    CheckIcon,
} from '@paygreen/paygreen-ui';
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom';
import { IsDesktop, IsMobile } from '../../utils/tools';
import { useNodeDimensions } from '../../utils/hooks';
import ProjectBreadCrumb from './ProjectBreadCrumb';

import IconByName from '../../utils/IconByName';
import { useAPIError, useUser } from '../../context';

import { ErrorBoundary, H1Title } from '../../components';

import { getProjectById, getStatByProject } from './service';

import ProjectCardDetail from './ProjectCardDetail';
import { SmallDataCard } from '../../components';
import SkeletonTextLine from '../../components/layout/SkeletonTextLine';
import getMainSdg from '../../utils/getMainSdg';

const ProjectDetail = ({ t }) => {
    const isMobile = IsMobile();
    const isDesktop = IsDesktop();
    let { projectId } = useParams();

    const { user } = useUser();

    const [project, setProjects] = useState([]);
    const [userContribution, setUserContribution] = useState(null);
    const [clientContribution, setClientContribution] = useState(null);
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const { addError } = useAPIError();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const [ref, { width }] = useNodeDimensions();

    const CategoryTitle = (props) => {
        const { category, icon } = props;

        let categoryIcon = null;

        if (icon) {
            categoryIcon = React.cloneElement(icon, {
                iconSize: 'lg',
                marginRight: 'sm',
            });
        }

        return (
            <InternalGrid
                displayType="grid"
                gridTemplateColumns={'repeat(2, fit-content(100%))'}
                justifyItems="start"
                alignItems="center"
                childrenMarginTop="md"
            >
                {categoryIcon}

                <Text htmlTag="strong" textSize="sm">
                    {category}

                    {props.children}
                </Text>
            </InternalGrid>
        );
    };

    const renderTitle = () => {
        if (isDefault === '0') {
            return (
                <HeartIcon
                    marginLeft="sm"
                    iconSize={isMobile ? 'md' : 'lg'}
                    title={renderToltipTitle()}
                />
            );
        }
        if (isDefault === '1' && userSelectedSDG) {
            return <CheckIcon title={renderToltipTitle()} iconSize="md" />;
        }
    };

    const fetchData = useCallback(() => {
        getProjectById(projectId)
            .then((res) => {
                setProjects(res.data);
            })
            .catch((error) => {
                if (error.response.status === 404) {
                    history.push('/404');
                }
                addError(
                    error?.response?.data.detail,
                    error?.response?.data.status,
                );
            });
    }, [history, projectId, addError]);

    const renderToltipTitle = () => {
        if (isDefault === '1') {
            return `${name} ${t(
                'contributions.globalView.globalCard.isFavorite',
            )}`;
        }

        if (isDefault === '0') {
            return `${name} ${t(
                'contributions.globalView.globalCard.isMyProject',
            )}`;
        }

        return null;
    };

    const getStatData = () => {
        const { carbonPrice } = project;

        if (carbonPrice) {
            getStatByProject(projectId)
                .then((res) => {
                    const { data } = res;
                    if (!data) {
                        setUserContribution(0);
                        setClientContribution(0);
                        return;
                    }

                    if (carbonPrice) {
                        const consumerContribution = (
                            Number.parseFloat(
                                data[0]?.carbonOffsetFromConsumer *
                                    project.carbonPrice,
                            ) / 100 || 0
                        ).toFixed(2);

                        const sellerContribution = Number.parseFloat(
                            (data[0]?.carbonOffset * project.carbonPrice) /
                                100 || 0,
                        ).toFixed(2);

                        setUserContribution(consumerContribution);
                        setClientContribution(sellerContribution);
                    }
                })
                .catch((error) => {
                    addError(
                        error?.response?.data.detail,
                        error?.response?.data.status,
                    );
                });
        }
    };

    useEffect(() => {
        isMounted.current = true;

        getMainSdg(user?.idUser?.toString());

        if (isMounted.current && project.length === 0) {
            fetchData();
        }
        getStatData();

        return () => {
            isMounted.current = false;
        };
    }, [project]);

    const { name, isDefault, country, carbonPrice, userSelectedSDG } = project;

    return (
        <>
            <div style={{ flexDirection: 'column' }} ref={ref}>
                <ProjectBreadCrumb />

                <SkeletonItem
                    colorWab="grey10"
                    radiusSize="sm"
                    blockWidth="auto"
                    isLoading={isLoading}
                >
                    <InternalGrid
                        displayType="flex"
                        alignItems="center"
                        justifyItems="start"
                        hasStaticWidth={false}
                    >
                        <H1Title title={name} marginBottom="none" />
                        <div>{renderTitle()}</div>
                    </InternalGrid>
                </SkeletonItem>

                <InternalGrid
                    hasStaticWidth={false}
                    justifyContent="flex-start"
                >
                    <SkeletonTextLine
                        colorWab="grey10"
                        radiusSize="sm"
                        blockWidth="xs"
                        isLoading={!country && !carbonPrice}
                    >
                        <InternalGrid
                            justifyContent="flex-start"
                            alignItems="center"
                            justifyItems="start"
                        >
                            <CategoryTitle
                                category={t(`countries:${country}`)}
                                icon={
                                    <MarkerIcon
                                        colorPallet="wab"
                                        colorWab="grey40"
                                    />
                                }
                            />
                        </InternalGrid>

                        <InternalGrid
                            justifyContent="flex-start"
                            alignItems="center"
                            justifyItems="start"
                            childrenMarginLateral="md"
                        >
                            <CategoryTitle
                                category={carbonPrice / 100}
                                marginRight="xs"
                                icon={
                                    <CoinsIcon
                                        colorPallet="wab"
                                        colorWab="grey40"
                                    />
                                }
                            >
                                {t(
                                    'contributions.carbonProjects.projectCard.carbonPrice',
                                )}
                            </CategoryTitle>
                        </InternalGrid>
                    </SkeletonTextLine>
                </InternalGrid>

                {!isDesktop && (
                    <InternalGrid
                        displayType="flex"
                        childrenFlex={1}
                        justifyItems="start"
                        childrenMarginLateral="xs"
                        alignItems="flex-start"
                        justifyContent="center"
                        childrenMarginTop="md"
                    >
                        <ErrorBoundary>
                            <SkeletonItem
                                colorWab="grey10"
                                radiusSize="sm"
                                isLoading={userContribution === null}
                            >
                                <SmallDataCard
                                    blockWidth="none"
                                    colorPallet="theme"
                                    colorTheme="secondary"
                                    title={t(
                                        'contributions.globalView.globalCard.yourCustomerPaid',
                                    )}
                                    icon={<CoinsIcon />}
                                    unit="€"
                                    value={userContribution || 0}
                                />
                            </SkeletonItem>

                            <SkeletonItem
                                colorWab="grey10"
                                radiusSize="sm"
                                isLoading={clientContribution === null}
                            >
                                <SmallDataCard
                                    blockWidth="none"
                                    colorPallet="theme"
                                    colorTheme="primary"
                                    title={t(
                                        'contributions.globalView.globalCard.youPaid',
                                    )}
                                    icon={<CoinsIcon />}
                                    unit="€"
                                    value={clientContribution}
                                >
                                    <RouterLink to="/contributions/my-contributions">
                                        <DaButton
                                            buttonSize="sm"
                                            gradient="theme"
                                            icon={
                                                <IconByName name="CoinsIcon" />
                                            }
                                        >
                                            {t(
                                                'contributions.contributeButton',
                                            )}
                                        </DaButton>
                                    </RouterLink>
                                </SmallDataCard>
                            </SkeletonItem>
                        </ErrorBoundary>
                    </InternalGrid>
                )}

                <InternalGrid
                    hasStaticWidth={true}
                    displayType={
                        width > ThemeDefault.breakpoint.md ? 'flex' : 'grid'
                    }
                    alignItems="flex-start"
                    gridTemplateColumns="1fr"
                    justifyItems="start"
                >
                    <InternalGrid
                        displayType={
                            isDesktop && width < ThemeDefault.breakpoint.xs
                                ? 'grid'
                                : 'flex'
                        }
                        gridTemplateColumns="46px 1fr"
                        alignItems="flex-start"
                        justifyItems="start"
                        childrenMarginTop="md"
                    >
                        <ErrorBoundary>
                            <ModalProvider>
                                <ProjectCardDetail
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    project={project}
                                    blockWidth="lg"
                                    width={width}
                                    fetchData={fetchData}
                                />
                            </ModalProvider>
                        </ErrorBoundary>
                    </InternalGrid>

                    {isDesktop && (
                        <InternalGrid
                            style={{
                                flex: 1,
                                width: '100%',
                            }}
                            displayType={
                                isDesktop && width > ThemeDefault.breakpoint.lg
                                    ? 'grid'
                                    : 'flex'
                            }
                            alignItems="flex-start"
                            gridTemplateColumns="50%"
                            childrenMarginTop="md"
                            childrenMarginBottom="sm"
                            childrenMarginLateral="md"
                            justifyContent="center"
                        >
                            <ErrorBoundary>
                                <SkeletonItem
                                    colorWab="grey10"
                                    radiusSize="sm"
                                    isLoading={userContribution === null}
                                >
                                    <SmallDataCard
                                        blockWidth="xs"
                                        colorPallet="theme"
                                        colorTheme="secondary"
                                        title={t(
                                            'contributions.globalView.globalCard.yourCustomerPaid',
                                        )}
                                        icon={<CoinsIcon />}
                                        unit="€"
                                        value={userContribution || 0}
                                    />
                                </SkeletonItem>

                                <SkeletonItem
                                    colorWab="grey10"
                                    radiusSize="sm"
                                    isLoading={clientContribution === null}
                                >
                                    <SmallDataCard
                                        blockWidth="xs"
                                        colorPallet="theme"
                                        colorTheme="primary"
                                        title={t(
                                            'contributions.globalView.globalCard.youPaid',
                                        )}
                                        icon={<CoinsIcon />}
                                        unit="€"
                                        value={clientContribution}
                                    >
                                        <RouterLink to="/contributions/my-contributions">
                                            <DaButton
                                                buttonSize="sm"
                                                gradient="theme"
                                                icon={
                                                    <IconByName name="CoinsIcon" />
                                                }
                                            >
                                                {t(
                                                    'contributions.contributeButton',
                                                )}
                                            </DaButton>
                                        </RouterLink>
                                    </SmallDataCard>
                                </SkeletonItem>
                            </ErrorBoundary>
                        </InternalGrid>
                    )}
                </InternalGrid>
            </div>
        </>
    );
};

export default withTranslation('common')(ProjectDetail);
